import React, { useMemo, useState } from "react";
import { useBetSlip } from "../providers/BetSlipProvider";
import StakeInput from "./StakeInput";
import VirtualKeyboard from "./VirtualKeyboard";
import WinBoost from "./WinBoost";
import { ErrorBar } from "./ErrorBar";
import { useI18n } from "../providers/I18nProvider";
import { BetSlipMode, EventStatus, Layout, OddStatus } from "../types/enums";
import { buildEventPageUrl } from "../shared/utils/bet-slip";

const Selections = () => {
  const { betSlipState, removeSelection, updateStake, config, stakes, selectedBonus, onNavigate } =
    useBetSlip();
  const { t, tHtml } = useI18n();
  const [activeKeyboard, setActiveKeyboard] = useState<string | null>(null);
  const [keyboardValue, setKeyboardValue] = useState("");

  const useVirtualKeyboard =
    config.kiosk.isKiosk || config.kiosk.layout === Layout.Mobile;

  const handleRemoveSelection = (selection: any) => {
    removeSelection(selection);
  };

  const handleEventTitleClick = (
    selection: IBetSlipSelection,
    isDisabled: boolean,
  ) => {
    if (!config.brand.allowEventClickNavigation || isDisabled) {
      return;
    }
    const isLive = selection.event?.st === EventStatus.Live || selection.isLive;
    onNavigate(buildEventPageUrl(config.culture, selection.event, isLive));
  };

  const handleStakeChange = (selectionKey: string, value: string) => {
    const numericValue = parseFloat(value) || 0;
    updateStake(selectionKey, numericValue);
  };

  const handleStakeInputFocus = (selectionKey: string) => {
    if (useVirtualKeyboard) {
      if (activeKeyboard !== selectionKey) {
        setActiveKeyboard(selectionKey);
        setKeyboardValue(
          stakes[selectionKey] > 0 ? stakes[selectionKey].toString() : "",
        );
      }
    }
  };

  const handleKeyboardValueChange = (selectionKey: string, value: string) => {
    setKeyboardValue(value);
    const numericValue = parseFloat(value) || 0;
    updateStake(selectionKey, numericValue);
  };

  const handleKeyboardClose = () => {
    setActiveKeyboard(null);
  };

  const hasOddsChanges = useMemo(() => {
    if (betSlipState.acceptOddsChanges) return false;
    return betSlipState.selections.some((selection) => {
      const hasOddChange =
        selection.oldOddPrice && selection.oldOddPrice !== selection.oddPrice;
      const hasStatusChange =
        selection.betStatus === OddStatus.Suspended ||
        selection.betStatus === OddStatus.Closed;
      return hasOddChange || hasStatusChange;
    });
  }, [betSlipState.acceptOddsChanges, betSlipState.selections]);

  const hasClosedOrSuspendedSelections = useMemo(() => {
    return betSlipState.selections.some(
      (sel) =>
        sel.betStatus === OddStatus.Suspended ||
        sel.betStatus === OddStatus.Closed,
    );
  }, [betSlipState.selections]);

  const errorBarMessage = useMemo(() => {
    if (hasClosedOrSuspendedSelections || hasOddsChanges)
      return t("loadBetSelectionChanges");
    return null;
  }, [hasClosedOrSuspendedSelections, hasOddsChanges, t]);

  if (betSlipState.selections.length === 0) {
    return null;
  }

  return (
    <div>
      {errorBarMessage && <ErrorBar message={errorBarMessage} />}

      {/* Win Boost Component */}
      <WinBoost />

      {/* Selections List */}
      {betSlipState.selections.map((selection) => {
        // Check for odds changes and status
        const hasOddsChanged =
          selection.oldOddPrice != null &&
          selection.oldOddPrice > 0 &&
          selection.oldOddPrice !== selection.oddPrice;
        const isClosed = selection.betStatus === OddStatus.Closed;
        const isSuspended = selection.betStatus === OddStatus.Suspended;
        const isOddsIncreased =
          hasOddsChanged && selection.oddPrice > selection.oldOddPrice;
        const isOddsDecreased =
          hasOddsChanged && selection.oddPrice < selection.oldOddPrice;
        const showOddsChangeVisuals =
          hasOddsChanged && !betSlipState.acceptOddsChanges;

        return (
          <React.Fragment key={selection.key}>
            <div className="relative border-b border-solid border-betslip-border overflow-hidden">
              {(isClosed || isSuspended) && (
                <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                  <span className="text-gray-500 text-20">
                    {isClosed ? t("BetSlipStatus-2") : t("BetSlipStatus-1")}
                  </span>
                </div>
              )}
              <div className="grid grid-cols-[minmax(0,_1fr)_45px] sm:grid-cols-[minmax(0,_1fr)_55px]">
                {/* Main content area */}
                <div
                  className={`pr-2 pl-4 py-3 ${isClosed || isSuspended ? "opacity-30" : ""}`}
                >
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2 justify-between">
                      <div
                        className={`text-md truncate ${!isClosed && !isSuspended && config.brand.allowEventClickNavigation ? "cursor-pointer" : ""}`}
                        onClick={() =>
                          handleEventTitleClick(
                            selection,
                            isClosed || isSuspended,
                          )
                        }
                      >
                        {t(selection.homeName as any)} -{" "}
                        {t(selection.awayName as any)}
                      </div>
                      <div className="flex flex-col items-end gap-3">
                        {/* New price with arrow indicator and background */}
                        <div className="relative bg-betslip-odds-bg text-betslip-odds-text text-md border border-solid border-betslip-odds-border rounded-[2px] px-2 min-w-[41px] h-[21px] text-center overflow-visible flex items-center gap-1">
                          {selection.boostedOdd && (
                            <i className="au-m-ico-l au-i-co au-i-boosted-odd"></i>
                          )}
                          <span className="font-bold">
                            {selection.oddPrice.toFixed(2)}
                          </span>
                          {/* Arrow indicator - triangle at corner */}
                          {showOddsChangeVisuals && isOddsIncreased && (
                            <div className="absolute top-0 right-0 w-0 h-0 border-l-[12px] border-l-transparent border-t-[12px] border-success rounded-tr-sm"></div>
                          )}
                          {showOddsChangeVisuals && isOddsDecreased && (
                            <div className="absolute bottom-0 right-0 w-0 h-0 border-l-[12px] border-l-transparent border-b-[12px] border-danger rounded-br-sm"></div>
                          )}
                        </div>
                        {/* Old price with strikethrough - below the new price */}
                        {showOddsChangeVisuals && (
                          <div className="text-sm text-gray-500 line-through">
                            {selection.oldOddPrice.toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Odd with background and stake (for single mode) */}
                    <div className="flex gap-2 justify-between items-center">
                      <div className="flex flex-col gap-2 text-md truncate">
                        <div className="text-md truncate font-semibold">
                          {selection.betTypeName}
                          {selection.oddLine
                            ? ` ${selection.oddLine}`
                            : ""} - {selection.oddDisplayName}
                        </div>
                        <div className="text-sm">
                          {selection.event?.st === EventStatus.Live ||
                          selection.isLive ? (
                            <span
                              className="text-danger font-semibold"
                              dangerouslySetInnerHTML={tHtml(
                                "BetSlipEventLiveTime",
                              )}
                            />
                          ) : (
                            <span>
                              {new Date(selection.gameTime).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                },
                              )}{" "}
                              {new Date(selection.gameTime).toLocaleTimeString(
                                "en-GB",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Stake Input for Single Mode - inline with odd (hidden while a free bet is applied) */}
                      {betSlipState.mode === BetSlipMode.Single && !selectedBonus && (
                        <StakeInput
                          value={
                            stakes[selection.key] && stakes[selection.key] > 0
                              ? stakes[selection.key]
                              : undefined
                          }
                          onStakeChange={(value) =>
                            handleStakeChange(selection.key, value)
                          }
                          currency={config.user.currency}
                          placeholder={t("BetSlipSingleStake")}
                          onFocus={() => handleStakeInputFocus(selection.key)}
                          readOnly={useVirtualKeyboard}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Full-height X button */}
                <button
                  onClick={() => handleRemoveSelection(selection)}
                  className="border-l border-betslip-border text-gray-400 flex items-center justify-center"
                >
                  <i className="au-m-ico-c au-i-x-thin text-10"></i>
                </button>
              </div>
            </div>

            {/* Virtual Keyboard - Appears right after this selection when active */}
            {betSlipState.mode === BetSlipMode.Single &&
              useVirtualKeyboard &&
              activeKeyboard === selection.key && (
                <div className="w-full overflow-hidden transition-all duration-300 ease-in-out animate-slideDown">
                  <div className="px-4 mt-3">
                    <VirtualKeyboard
                      value={keyboardValue}
                      onValueChange={(value) =>
                        handleKeyboardValueChange(selection.key, value)
                      }
                      onClose={handleKeyboardClose}
                      currency={config.user.currency}
                      chips={(
                        config.brand.currencyConfigurations?.[
                          config.user.currencyId
                        ]?.keyboard?.chips?.items ??
                        (config.kiosk.keyboard?.chips?.items as any)
                      )?.map(
                        (item: {
                          action: string;
                          value: number;
                          content: string;
                        }) => ({
                          action:
                            item.action === "+" || item.action === "set"
                              ? "add"
                              : "clear",
                          value: item.value,
                          label: item.content.toString(),
                        }),
                      )}
                    />
                  </div>
                </div>
              )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default Selections;
